import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { useStoreAuth } from '@/context/StoreAuthContext';
import { useBrokerAccess } from '@/hooks/useBrokerAccess';
import useBrandSettings from '@/hooks/useBrandSettings';
import { Button } from '@/components/ui/button';
import { EnvironmentCards } from '@/components/auth/EnvironmentCards';
import { setEnvironmentChoice, type WorkEnvironment } from '@/lib/environmentChoice';
import { getUserNetwork } from '@/utils/scopeValidator';

export default function ChooseEnvironment() {
  const { user, status, retry, logout } = useStoreAuth();
  const { hasBrokerAccess, isBrokerOnly, isLoading, isError, refetch } = useBrokerAccess();
  const { data: brand } = useBrandSettings();
  const navigate = useNavigate();
  const teamNetwork = useQuery({
    queryKey: ['environment-team-network', user?.id],
    enabled: !!user && status === 'authenticated' && !isLoading && hasBrokerAccess && !isBrokerOnly && !user.isPlatformAdmin,
    queryFn: async () => {
      if (!user) throw new Error('Usuário indisponível');
      const network = await getUserNetwork(user.id);
      if (!network?.networkName) throw new Error('Rede da equipe indisponível');
      return network;
    },
    staleTime: 60_000,
  });

  if (status === 'booting' || isLoading) return (
    <div className="dark flex min-h-screen items-center justify-center bg-background text-foreground" role="status" aria-label="Carregando ambientes">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
  if (status === 'error' || isError) return (
    <div className="dark flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center text-foreground">
      <p className="text-foreground">Não foi possível carregar seus ambientes.</p>
      <Button variant="outline" onClick={() => { void retry(); void refetch(); }}>Tentar novamente</Button>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (user.isPlatformAdmin) return <Navigate to="/super-admin" replace />;
  if (isBrokerOnly) return <Navigate to="/portal-corretor" replace />;
  if (!hasBrokerAccess) return <Navigate to="/inicio" replace />;

  const choose = (environment: WorkEnvironment) => {
    setEnvironmentChoice(user.id, environment);
    navigate(environment === 'team' ? '/inicio' : '/portal-corretor', { replace: true });
  };

  return (
    <div className="dark flex min-h-screen flex-col bg-background text-foreground">
      <header className="flex items-center justify-between gap-4 border-b border-border px-6 py-4">
        <div className="flex min-w-0 items-center gap-3">
          {(brand?.symbolLight || brand?.symbolDark) && <img src={brand.symbolLight || brand.symbolDark} alt="" className="h-9 w-9 object-contain" />}
          <span className="truncate text-lg font-semibold">{brand?.platformName || 'AutoFluxo'}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void logout()}><LogOut />Sair</Button>
      </header>
      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center gap-8 px-6 py-12">
        <div className="space-y-3">
          <p className="break-words text-sm text-muted-foreground">Bem-vindo, {user.name}! 👋</p>
          <h1 className="text-3xl font-semibold">Qual ambiente você deseja acessar?</h1>
        </div>
        {teamNetwork.isError ? (
          <div className="space-y-4">
            <p className="text-muted-foreground">Não foi possível carregar a rede da sua equipe.</p>
            <Button variant="outline" onClick={() => void teamNetwork.refetch()}>Tentar novamente</Button>
          </div>
        ) : teamNetwork.data ? (
          <EnvironmentCards networkName={teamNetwork.data.networkName} onChoose={choose} />
        ) : (
          <div className="flex min-h-48 items-center justify-center" role="status" aria-label="Carregando rede da equipe">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}
      </main>
    </div>
  );
}